import { useEvent, useState } from 'hooks'
import { cn } from 'utils'
import { getAllScrolledObjects, getScrollToViewElementSelector, getShiftTriggerForScrollSelector } from './selector'

type MovableObject = {
	elementWithAffectedScroll: HTMLElement
	shiftLeftTrigger?: HTMLElement
	shiftRightTrigger?: HTMLElement
}

type State = {
	elementsWithAffectedScroll?: NodeListOf<HTMLElement>
}

export const Scroller = (() => {
	const { state, setState } = useState<State>()

	const clickHandler = (trigger: HTMLElement, affectedObject: HTMLElement, direction: string) => {
		const click = useEvent(trigger, 'click')

		click.register(() => {
			const scrollBy = affectedObject.offsetWidth / 4
			if (direction === 'right') {
				affectedObject.scrollBy({ left: scrollBy, behavior: 'smooth' })
			}

			if (direction === 'left') {
				affectedObject.scrollBy({ left: -scrollBy, behavior: 'smooth' })
			}
		})
	}

	const onScrollHandler = (affectedObject: HTMLElement, leftTrigger?: HTMLElement, rightTrigger?: HTMLElement) => {
		const onscroll = useEvent(affectedObject, 'scroll')
		const scrollToViewElement = getScrollToViewElementSelector(affectedObject)

		onscroll.register(() => {
			if (scrollToViewElement) {
				scrollToViewElement.style.left = `${affectedObject.scrollLeft}px`
			}

			if (leftTrigger) {
				cn.removeClass(leftTrigger, 'invisible')

				if (affectedObject.scrollLeft <= 0) {
					cn.addClass(leftTrigger, 'invisible')
				}
			}

			if (rightTrigger) {
				cn.removeClass(rightTrigger, 'invisible')

				if (affectedObject.scrollLeft >= affectedObject.scrollWidth - affectedObject.clientWidth) {
					cn.addClass(rightTrigger, 'invisible')
				}
			}
		})
	}

	const initMovableObjects = () => {
		const movableObjects = getAllMovableObjects()
		if (!movableObjects) return

		for (let i = 0; i < movableObjects.length; i++) {
			const movableObject = movableObjects[i]
			if (!movableObject.elementWithAffectedScroll) continue

			onScrollHandler(
				movableObject.elementWithAffectedScroll,
				movableObject.shiftLeftTrigger,
				movableObject.shiftRightTrigger,
			)

			if (movableObject.shiftLeftTrigger) {
				clickHandler(movableObject.shiftLeftTrigger, movableObject.elementWithAffectedScroll, 'left')
			}

			if (movableObject.shiftRightTrigger) {
				clickHandler(movableObject.shiftRightTrigger, movableObject.elementWithAffectedScroll, 'right')
			}
		}
	}

	const getAllMovableObjects = (): Array<MovableObject> => {
		const { elementsWithAffectedScroll } = state
		if (!elementsWithAffectedScroll) return []

		const movableObjects = []
		for (let i = 0; i < elementsWithAffectedScroll.length; i++) {
			const elementWithAffectedScroll = elementsWithAffectedScroll[i]
			if (!elementWithAffectedScroll) continue

			const shiftLeftTrigger = getShiftTriggerForScrollSelector(elementWithAffectedScroll, 'left')
			const shiftRightTrigger = getShiftTriggerForScrollSelector(elementWithAffectedScroll, 'right')

			movableObjects.push({
				elementWithAffectedScroll,
				shiftLeftTrigger,
				shiftRightTrigger,
			})
		}

		return movableObjects
	}

	const init = () => {
		setState({ ...getAllScrolledObjects() })
		initMovableObjects()
	}

	return {
		init,
	}
})()
